// @flow
/** @jsx h */

import { h } from 'preact'

import styles from './LivePollWidget.css'

import { LivePoll } from './components/LivePoll/LivePoll'
import { Loader } from '../../components/Loader/Loader'
import { Error } from '../../components/Error/Error'
import { WithVotingState } from './WithVotingState'
import { WithPollState } from './WithPollState'

import type { Poll, AuthConfig, Breakpoint, Vote } from '../../types'
import type { LivePollWidgetRenderProps, WithVotingStateRenderProps, WithResultStateRenderProps } from './types'

import { WithResultState } from './WithResultState'
import { bem } from '../../helpers/bem'
import { themeToClassName } from '../../helpers/themeToClassName'
import
{
  breakpointToClassName,
  getCurrentBreakpoint
} from '../../helpers/breakpointToClassName'
import { StemningIcon } from '../../components/StemningIcon/StemningIcon'
import { AriaLive } from './components/LivePoll/AriaLive/AriaLive'
import { alternativeIdToAlternativeText } from './helpers/alternativeUtils'
import { RetryButton } from './components/LivePoll/RetryButton/RetryButton'
import { debounce } from '../../helpers/debounce'
import { PureComponent } from '../../helpers/pure-components'

type Props = {
  clientId: number,
  theme?: ?string,
  authConfig: AuthConfig,
  showResult?: boolean,
  resultMessage?: string
}

type State = {
  poll?: Poll,
  error?: ?Error,
  breakpoint: Breakpoint
}

export class LivePollWidget extends PureComponent<Props, State> {
  debouncedResize = null
  rootEl: ?HTMLElement = null
  state = {
    breakpoint: 'M'
  }

  getChildContext () {
    return {
      theme: this.props.theme,
      isInteractive: true,
      currentBreakpoint: this.state.breakpoint
    }
  }

  componentDidMount = () => {
    this.handleResize()
    this.debouncedResize = debounce(this.handleResize, 16.667)
    window.addEventListener('resize', this.debouncedResize)
  }

  componentWillUnmount = () => {
    window.removeEventListener('resize', this.debouncedResize)
  }

  handleResize = () => {
    if (!this.rootEl) {
      return
    }

    const width = this.rootEl.offsetWidth
    this.setState({ breakpoint: getCurrentBreakpoint(width) })
  }

  handleRetryButtonClicked () {
    window.location.reload(true)
  }

  render () {
    const {
      clientId,
      authConfig
    } = this.props

    return (
      <WithPollState
        authConfig={authConfig}
        clientId={clientId}
        render={this.renderWithPollState}
      />
    )
  }
  renderWithPollState = (arg: LivePollWidgetRenderProps) => {
    const { error: loadPollError, poll, loading } = arg
    const { breakpoint } = this.state
    const {
      clientId,
      theme,
      authConfig
    } = this.props

    return (
      <div
        ref={el => (this.rootEl = el)}
        className={`${getBaseColorClass(theme)} ${bem(
          styles.livePollWidget,
          themeToClassName(theme),
          breakpointToClassName(breakpoint)
        )}`}
      >

        {loadPollError && (
          <Error>
            <div style={{ marginBottom: '10px' }}>
              Avstemningen er ikke tilgjengelig akkurat nå.
            </div>
            <RetryButton onClick={this.handleRetryButtonClicked}>
              Prøv å laste på nytt
            </RetryButton>
          </Error>
        )}

        {loading && (
          <Loader>
            <StemningIcon animate='infinite' />
            <p>Laster innhold</p>
          </Loader>
        )}

        {(!loading && !loadPollError && poll) && (
          <WithVotingState
            clientId={clientId}
            poll={poll}
            authConfig={authConfig}
            render={arg => this.renderWithVotingState(arg, poll)}
          />
        )}
      </div>
    )
  }

  renderWithVotingState = (arg: WithVotingStateRenderProps, poll: Poll) => {
    const {
      hasVoted,
      vote,
      error: voteError,
      onRegisterVote,
      isDisplayVoteConfirmedMessage
    } = arg

    const { clientId,
      authConfig } = this.props

    const resultState = {
      onRegisterVote,
      hasVoted,
      voteError,
      isDisplayVoteConfirmedMessage,
      vote
    }

    return <WithResultState
      clientId={clientId}
      authConfig={authConfig}
      poll={poll}
      render={arg => this.renderWithResultState(arg, resultState, poll)}
    />
  }

  renderWithResultState = (arg: WithResultStateRenderProps, rest: WithResultStateRestProps, poll: Poll) => {
    const {
      result,
      error: resultError,
      lastUpdatedTime
    } = arg

    const {
      showResult,
      resultMessage
    } = this.props

    const { onRegisterVote,
      hasVoted,
      voteError,
      isDisplayVoteConfirmedMessage,
      vote } = rest
    return <LivePoll
      poll={poll}
      onRegisterVote={onRegisterVote}
      hasVoted={hasVoted}
      voteError={voteError}
      resultError={resultError}
      result={result}
      lastUpdatedTime={lastUpdatedTime}
      showResult={showResult}
      resultMessage={resultMessage}
    >
      <AriaLive type='assertive'>
        {isDisplayVoteConfirmedMessage &&
          `Din stemme på ${alternativeIdToAlternativeText(
            vote.alternativeId,
            poll.alternatives
          )} ble registrert`}
      </AriaLive>
    </LivePoll>
  }
}

type WithResultStateRestProps = {
  onRegisterVote: Function,
  hasVoted: boolean,
  voteError: Error,
  isDisplayVoteConfirmedMessage: boolean,
  vote: Vote
}

function getBaseColorClass (skin: ?string) {
  if (skin === 'sport') {
    return 'nrk-color-invert'
  } else {
    return 'nrk-color-base'
  }
}



// WEBPACK FOOTER //
// ./src/shared/widgets/LivePoll/LivePollWidget.js